// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
/**
 * File-type detection helpers.
 *
 * Helpers check BOTH the MIME content_type AND the filename extension so
 * uploads with missing/incorrect content_type still classify correctly.
 *
 * The input type is the minimal structural shape (name + optional
 * content_type) so this works for global files, customer attachments,
 * and any other source that has those two fields.
 */

/**
 * Minimal file shape needed by these predicates. Anything that has a
 * `name` and an optional `content_type` qualifies.
 */
export type FileLike = {
  name: string
  content_type?: string
}

export function getFileExtension(filename: string): string {
  return filename.split(".").pop()?.toLowerCase() || ""
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes"
  const k = 1024
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
}

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp", "ico"]
const VIDEO_EXTENSIONS = ["mp4", "webm", "ogg", "avi", "mov", "wmv", "flv", "mkv"]
const AUDIO_EXTENSIONS = ["mp3", "wav", "ogg", "aac", "flac", "m4a"]
const EXCEL_EXTENSIONS = ["xls", "xlsx", "xlsm", "xlsb"]
const EXCEL_CONTENT_TYPES = [
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]
const WORD_EXTENSIONS = ["doc", "docx"]
const WORD_CONTENT_TYPES = [
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]
const PPT_EXTENSIONS = ["ppt", "pptx"]
const PPT_CONTENT_TYPES = [
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
]
const TEXT_EXTENSIONS = [
  "txt",
  "md",
  "json",
  "xml",
  "csv",
  "log",
  "js",
  "ts",
  "tsx",
  "jsx",
  "css",
  "html",
  "py",
  "rb",
  "php",
  "sql",
  "yml",
  "yaml",
]

export function isImageFile(file: FileLike): boolean {
  return file.content_type?.startsWith("image/") || IMAGE_EXTENSIONS.includes(getFileExtension(file.name))
}

export function isPdfFile(file: FileLike): boolean {
  return file.content_type === "application/pdf" || getFileExtension(file.name) === "pdf"
}

export function isVideoFile(file: FileLike): boolean {
  return file.content_type?.startsWith("video/") || VIDEO_EXTENSIONS.includes(getFileExtension(file.name))
}

export function isAudioFile(file: FileLike): boolean {
  return file.content_type?.startsWith("audio/") || AUDIO_EXTENSIONS.includes(getFileExtension(file.name))
}

export function isExcelFile(file: FileLike): boolean {
  return (
    EXCEL_CONTENT_TYPES.some((t) => file.content_type === t) ||
    EXCEL_EXTENSIONS.includes(getFileExtension(file.name))
  )
}

export function isCsvFile(file: FileLike): boolean {
  return file.content_type === "text/csv" || getFileExtension(file.name) === "csv"
}

export function isWordFile(file: FileLike): boolean {
  return (
    WORD_CONTENT_TYPES.some((t) => file.content_type === t) ||
    WORD_EXTENSIONS.includes(getFileExtension(file.name))
  )
}

/** Modern XML-based DOCX (parseable in-browser). */
export function isDocxFile(file: FileLike): boolean {
  return (
    file.content_type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    getFileExtension(file.name) === "docx"
  )
}

/** Legacy binary DOC (cannot be parsed in-browser). */
export function isDocFile(file: FileLike): boolean {
  return file.content_type === "application/msword" || getFileExtension(file.name) === "doc"
}

export function isPowerPointFile(file: FileLike): boolean {
  return (
    PPT_CONTENT_TYPES.some((t) => file.content_type === t) ||
    PPT_EXTENSIONS.includes(getFileExtension(file.name))
  )
}

/** Modern XML-based PPTX (parseable in-browser). */
export function isPptxFile(file: FileLike): boolean {
  return (
    file.content_type === "application/vnd.openxmlformats-officedocument.presentationml.presentation" ||
    getFileExtension(file.name) === "pptx"
  )
}

/** Legacy binary PPT (cannot be parsed in-browser). */
export function isPptFile(file: FileLike): boolean {
  return file.content_type === "application/vnd.ms-powerpoint" || getFileExtension(file.name) === "ppt"
}

export function isDocumentFile(file: FileLike): boolean {
  return isExcelFile(file) || isCsvFile(file) || isWordFile(file) || isPowerPointFile(file)
}

export function isTextFile(file: FileLike): boolean {
  return file.content_type?.startsWith("text/") || TEXT_EXTENSIONS.includes(getFileExtension(file.name))
}
